import React from "react";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/modern/components/ui/input-group";
import { useSwivuAppSearch } from "@/react/hooks/useSwivuAppSearch";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { cn } from "@/modern/utils";
import { pluralize } from "@/other/utils/otherUtils";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";

const GRID_ICONS = {
  resources: SWIVU_STANDARD_ICONS[VIEW_TYPES.resources],
  tasks: SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks]
};

export const AppSearchBar = ({ isKanbanMode }: { isKanbanMode: boolean }) => {
  const { searchText, searchCount, activeGrid, isSearchable, hasMultipleGrids, searchableGrids, search, clear, setActiveGrid, gotoNextHit, gotoPrevHit } = useSwivuAppSearch();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    search(e.target.value);
  };

  // Prevent buttons from stealing focus from the grid
  const preventFocusChange = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  if (!isSearchable || isKanbanMode) return null;

  const hasResults = searchCount > 0;
  const hasSearchText = searchText.trim().length > 0;

  return (
    <div className="flex w-full items-center gap-2">
      {/* Main Search Input with integrated grid selector */}
      <InputGroup className="h-9 w-full !bg-background/60 border border-border rounded-lg shadow-sm transition-colors duration-200 focus-within:!ring-0 focus-within:border-border">
        <InputGroupAddon className={cn("text-muted-foreground p-0 h-full flex items-center", hasMultipleGrids ? "pl-0" : "pl-2")}>
          {hasMultipleGrids && (
            <ButtonGroup className="h-full">
              {searchableGrids.map(({ id }, index) => {
                const isActive = activeGrid === id;
                const displayName = id.charAt(0).toUpperCase() + id.slice(1);
                const iconClass = GRID_ICONS[id as keyof typeof GRID_ICONS];
                const isFirst = index === 0;
                const isLast = index === searchableGrids.length - 1;
                return (
                  <Button
                    key={id}
                    type="button"
                    onClick={() => setActiveGrid(id)}
                    onMouseDown={preventFocusChange}
                    variant={isActive ? "default" : "ghost"}
                    size="sm"
                    className={cn(
                      "h-full px-3 text-base font-normal transition-colors duration-200 flex items-center !gap-1.5 border-r border-border/50 rounded-none",
                      isFirst && "rounded-l-md rounded-r-none rounded-tr-none rounded-br-none",
                      isLast && "!rounded-none !rounded-l-none !rounded-r-none !rounded-t-none !rounded-b-none !rounded-tr-none !rounded-br-none !rounded-tl-none !rounded-bl-none border-r-0",
                      isActive ? "bg-foreground/10 hover:bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                    )}
                  >
                    <i className={cn(iconClass, "text-xs")} />
                    <span>{displayName}</span>
                  </Button>
                );
              })}
            </ButtonGroup>
          )}
        </InputGroupAddon>

        <InputGroupInput
          className="h-full min-w-32 !border-0 !shadow-none !outline-none !ring-0 !ring-offset-0 px-2 text-sm placeholder:text-muted-foreground/50 placeholder:text-sm"
          placeholder="Search..."
          value={searchText}
          onChange={handleSearchChange}
        />

        {/* Right Side Controls */}
        <div className="flex items-center gap-2 pr-1.5">
          {/* Clear Button */}
          {hasSearchText && (
            <InputGroupButton
              size="icon-xs"
              className="h-6 w-6 rounded-full transition-colors duration-200 hover:bg-background/80 text-foreground"
              onClick={clear}
              onMouseDown={preventFocusChange}
              title="Clear search"
            >
              <X className="h-3 w-3" />
            </InputGroupButton>
          )}

          {/* Divider - Only visible when there's search text */}
          {hasSearchText && <div className="h-4 w-px bg-foreground/20 transition-opacity duration-200" />}

          {/* Search Status */}
          {hasSearchText && (
            <div className="flex min-w-20 items-center justify-center">
              <Badge
                variant="muted"
                size="sm"
                className={cn(
                  "animate-in fade-in zoom-in-95 px-2 py-0.5 text-xs font-medium tabular-nums transition-colors duration-200 border-transparent",
                  hasResults ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {searchCount} {pluralize(searchCount, "Result")}
              </Badge>
            </div>
          )}

          {/* Navigation Buttons - Always rendered but disabled when no results */}
          <div className="flex items-center gap-0.5 rounded-md bg-background/60 p-0.5">
            <InputGroupButton
              size="icon-xs"
              className={cn("h-5 w-5 rounded transition-colors duration-200 text-foreground", hasResults ? "hover:bg-background/80" : "pointer-events-none opacity-30")}
              onClick={gotoPrevHit}
              onMouseDown={preventFocusChange}
              title="Previous (Shift + F3)"
              disabled={!hasResults}
            >
              <ChevronUp className="h-3 w-3" />
            </InputGroupButton>
            <InputGroupButton
              size="icon-xs"
              className={cn("h-5 w-5 rounded transition-colors duration-200 text-foreground", hasResults ? "hover:bg-background/80" : "pointer-events-none opacity-30")}
              onClick={gotoNextHit}
              onMouseDown={preventFocusChange}
              title="Next (F3)"
              disabled={!hasResults}
            >
              <ChevronDown className="h-3 w-3" />
            </InputGroupButton>
          </div>
        </div>
      </InputGroup>
    </div>
  );
};
